import useResendEstimate from '@/api/mutations/estimates/useResendEstimate';
import useSendEstimate from '@/api/mutations/estimates/useSendEstimate';
import useGetEstimateEmailPreview from '@/api/queries/estimates/useGetEstimateEmailPreview';
import useGetEstimatePreview from '@/api/queries/estimates/useGetEstimatePreview';
import useGetMe from '@/api/queries/me/useGetMe';
import useGetMyAccountBranding from '@/api/queries/me/useGetMyAccountBranding';
import useGetProjectEstimate from '@/api/queries/project/useGetProjectEstimate';
import BackToEditingButton from '@/ui/components/estimatesAndProposals/draftSendEmail/BackToEditingButton';
import {
  buildPinnedRecipient,
  resolveRecipient,
  sortContacts,
  type Contact,
} from '@/ui/components/estimatesAndProposals/draftSendEmail/contacts';
import DraftSendEmailPreviewFieldset, {
  DRAFT_SEND_EMAIL_PREVIEW_SCHEMA,
  type DraftSendEmailPreviewValues,
} from '@/ui/components/estimatesAndProposals/draftSendEmail/DraftSendEmailPreviewFieldset';
import DraftSendEmailSubmitButton from '@/ui/components/estimatesAndProposals/draftSendEmail/DraftSendEmailSubmitButton';
import { buildDefaultEmailCopy } from '@/ui/components/estimatesAndProposals/draftSendEmail/emailCopy';
import EmailSuppressedAlert from '@/ui/components/estimatesAndProposals/draftSendEmail/EmailSuppressedAlert';
import {
  PanelBody,
  PanelHeaderBar,
} from '@/ui/components/estimatesAndProposals/draftSendEmail/panelLayout';
import { useBouncedEmailCorrection } from '@/ui/components/estimatesAndProposals/draftSendEmail/useBouncedEmailCorrection';
import { useDraftSendEmailSuppression } from '@/ui/components/estimatesAndProposals/draftSendEmail/useDraftSendEmailSuppression';
import RecipientPicker from '@/ui/components/RecipientPicker';
import { useSenderChoice } from '@/ui/components/senderChoice/useSenderChoice';
import EstimateFinancials from '@/ui/pages/estimateBuilderPage/components/EstimateFinancials';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form, useToastContext } from '@payaca/ambrosia';
import { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import EstimateHeaderIdentity from './EstimateHeaderIdentity';

export type SendEstimateViewProps = {
  projectId: string;
  estimateId: string;
  onBack?: () => void;
  /** Label for the back button. Defaults to "Back to editing". */
  backLabel?: string;
  onSent: () => void;
};

const SendEstimateView: FC<SendEstimateViewProps> = (props) => {
  const { projectId, estimateId, onBack, backLabel, onSent } = props;

  const { data: me, isPending: isLoading } = useGetMe();
  const companyName = me?.user.account.name ?? '';

  const { pushSuccess } = useToastContext();
  const sendEstimate = useSendEstimate({ projectId });
  const resendEstimate = useResendEstimate({ projectId });
  const { project, estimate } = useGetProjectEstimate(projectId, estimateId);
  const { data: branding } = useGetMyAccountBranding();
  const { emailPreview } = useGetEstimateEmailPreview(estimateId);
  const senderChoice = useSenderChoice(me?.emailSenderChoice);

  const isSent = estimate?.status === 'SENT';
  const { preview } = useGetEstimatePreview(isSent ? undefined : estimateId);
  const imagePending = !isSent && preview?.pendingSolarLayoutImage;
  const sentState = estimate?.bouncedAt ? 'bounced' : 'resend';
  const state = isSent ? sentState : 'send';

  const [recipientOverride, setRecipientOverride] = useState<Contact | null>(
    null
  );
  const contacts = sortContacts(project?.customer?.contacts ?? []);
  // Once SENT, pin to the estimate's stored customerContact so the recipient
  // reflects who actually received it.
  const recipient = isSent
    ? buildPinnedRecipient(estimate?.customerContact)
    : resolveRecipient(contacts, recipientOverride);

  // Estimates don't support Account email copy templates yet, so this always
  // resolves to the no-template fallback greeting.
  const { intro: defaultIntro, outro: defaultOutro } = buildDefaultEmailCopy({
    template: '',
    recipientName: recipient?.name,
    reference: '',
    companyName,
    documentNoun: 'estimate',
  });

  const finaliseResend = (values: DraftSendEmailPreviewValues) => {
    resendEstimate.mutate(
      {
        id: estimateId,
        intro: values.intro.trim() || undefined,
        outro: values.outro.trim() || undefined,
      },
      {
        onSuccess: () => {
          pushSuccess(`Estimate resent to ${recipient?.name ?? 'customer'}.`);
          onSent();
        },
      }
    );
  };

  const { submitResend, isPending: isCorrectingEmail } =
    useBouncedEmailCorrection({ state, recipient, onResend: finaliseResend });

  const isSubmitting =
    sendEstimate.isPending || resendEstimate.isPending || isCorrectingEmail;

  const formMethods = useForm<DraftSendEmailPreviewValues>({
    defaultValues: {
      intro: '',
      outro: '',
      recipientEmail: '',
    },
    resolver: zodResolver(DRAFT_SEND_EMAIL_PREVIEW_SCHEMA),
  });

  const suppression = useDraftSendEmailSuppression({
    state,
    recipient,
    control: formMethods.control,
  });

  const handleSend = (values: DraftSendEmailPreviewValues) => {
    if (!recipient?.email || imagePending) return;
    sendEstimate.mutate(
      {
        id: estimateId,
        customerContactId: recipient.publicId,
        intro: values.intro.trim() || undefined,
        outro: values.outro.trim() || undefined,
      },
      {
        onSuccess: () => {
          pushSuccess(`Estimate sent to ${recipient.name ?? 'customer'}.`);
          onSent();
        },
      }
    );
  };

  const handleSubmit = (values: DraftSendEmailPreviewValues) => {
    if (isSubmitting) return;
    if (state === 'send') handleSend(values);
    else submitResend(values);
  };

  return (
    <Form
      methods={formMethods}
      onSubmit={handleSubmit}
      className="flex h-full min-h-0 flex-col"
    >
      <PanelHeaderBar>
        <PanelHeaderBar.Group>
          {onBack && (
            <BackToEditingButton
              onClick={onBack}
              disabled={isSubmitting}
              label={backLabel}
            />
          )}
          {/* Show the reference, status, and actions menu only when there's no
              Back button — i.e. a bounced Estimate that lands straight on
              resend. When the user came here via "Resend" from the overview,
              the Back button anchors them and the overview already carries the
              identity + actions, so it would be redundant clutter here. */}
          {state !== 'send' && !onBack && (
            <EstimateHeaderIdentity
              projectId={projectId}
              estimateId={estimateId}
            />
          )}
        </PanelHeaderBar.Group>

        <PanelHeaderBar.Actions>
          <RecipientPicker
            recipient={recipient}
            contacts={contacts}
            onSelectRecipient={
              state === 'send' ? setRecipientOverride : undefined
            }
          />

          <DraftSendEmailSubmitButton
            state={state}
            entityLabel="estimate"
            recipient={recipient}
            isLoading={isLoading}
            isSubmitting={isSubmitting}
            isSuppressed={suppression.isSuppressed}
            submitDisabledReason={
              imagePending ? 'The roof image is still being prepared.' : null
            }
          />
        </PanelHeaderBar.Actions>
      </PanelHeaderBar>

      <PanelBody>
        <EstimateFinancials projectId={projectId} estimateId={estimateId} />

        <PanelBody.Content>
          <EmailSuppressedAlert
            entityLabel="estimate"
            suppression={suppression}
          />

          <DraftSendEmailPreviewFieldset
            isLoading={isLoading}
            recipient={recipient}
            editableRecipient={state === 'bounced'}
            documentNoun="estimate"
            logoUrl={branding?.logoUrl}
            senderChoice={senderChoice}
            subject={emailPreview?.subject}
            defaultIntro={defaultIntro}
            defaultOutro={defaultOutro}
          />
        </PanelBody.Content>
      </PanelBody>
    </Form>
  );
};

export default SendEstimateView;
